import { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Card,
  FormHelperText,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik, Form, FormikProvider, FieldArray } from "formik";
import * as Yup from "yup";
import { useMutate } from "src/hooks/useFetch.hook";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import { Loader } from "../Loader";
import { fileToBase64 } from "src/utils/fileConvertBase64";
import { fileUpload } from "../ServiceRequest/serviceRequest.hook";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  addVehicleUrl,
  deleteVehicleUrl,
  getVehicleByVehicleId,
  updateVehicleUrl,
} from "src/react-query/endPoints";
import { queryKeys } from "src/react-query/queryKeys";
import { useSelectedCompany, useSelectedTeam } from "src/hooks/useSelectedTeam";
import { useDispatch } from "react-redux";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";

const VehicleDetailsForm = () => {
  const dispatch = useDispatch();
  const { vehicleId: id } = useParams(); // Customer ID (user_pk)
  const { teamId } = useSelectedTeam();
  const company = useSelectedCompany();
  const navigate = useNavigate();
  const { mutate, isLoading } = useMutate("vehicle");
  // const { data: vehicleData, isFetching } = useGetDetail("vehicles", id);
  const [uploading, setUploading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const fileUploadUrl = useMutation(fileUpload);
  const {
    data: vehicleData,
    isFetching,
    refetch,
  } = useQuery(
    [queryKeys.vehicles, company.id, id],
    () => getVehicleByVehicleId(company.id, id),
    {
      enabled: !!id, // Only fetch if `id` is available
      onSuccess: (data) => {
        setVehicles(data?.data || []);
        formik.setValues({ vehicles: data?.data || [] });
      },
      onError: (error) => {
        console.error('VehicleDetailForm: Error fetching vehicle details:', {
          error: error.message,
          stack: error.stack,
          vehicleId: id,
          companyId: company.id
        });
        if (
          error.message === "No token provided." ||
          error.message === "Invalid token."
        ) {
          // Redirect to login page if token is missing or invalid
          navigate("/login");
        }
      },
    }
  );

  // 🚀 File Upload Handler (Upload to S3)
  const handleFileUpload = async (event, field, index) => {
    const file = event.target.files[0];
    if (!file || file.type !== "application/pdf") {
      return alert("Only PDF files are allowed!");
    }

    setUploading(true);

    try {
      // Convert the file to base64
      const fileInBase64 = await fileToBase64(file);

      // Use the mutate method to upload the file
      fileUploadUrl.mutate(
        {
          image: fileInBase64,
          directory: "vehicle-documents", // Specify the directory in S3
        },
        {
          onSuccess: (response) => {
            // Update the Formik field with the uploaded file URL
            formik.setFieldValue(
              `vehicles[${index}].${field}`,
              response.data.data
            );
            setUploading(false);
          },
          onError: (error) => {
            console.error("File Upload Error:", error);
            setUploading(false);
          },
        }
      );
    } catch (error) {
      console.error("Error converting file to base64:", error);
      setUploading(false);
    }
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const vehicle = values.vehicles[0];
    const isUpdate = !!id;
    await mutate(
      {
        url: isUpdate
          ? updateVehicleUrl(company.id, id)
          : addVehicleUrl(company.id),
        method: isUpdate ? "PUT" : "POST",
        body: vehicle,
      },
      { onSuccess }
    );
    setSubmitting(false);
  };

  const onSuccess = () => {
    dispatch(
      tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
        detailsId: "",
        tabOpenedFor: "",
        tabOpened: "",
      })
    );
    navigate("/vehicles");
  };

  // 🚀 Formik Form
  const formik = useFormik({
    initialValues: {
      vehicles: vehicleData || [
        {
          id: "", // Vehicle ID (for update/delete)
          vehicle_type: "",
          make: "",
          model: "",
          variant: "",
          registration_number: "",
          chassis_number: "",
          engine_number: "",
          vin: "",
          license_plate_state: "",
          battery_capacity: "",
          motor_type: "",
          seating_capacity: "",
          color: "",
          body_type: "",
          last_service_date: "",
          service_history: "",
          next_service_date: "",
          warranty_details: "",
          roadside_assistance_provider: "",
          owner_name: "",
          owner_contact: "",
          usage_type: "",
          odometer_reading: "",
          vehicle_insaurance_provider: "",
          insaurance_policy_number: "",
          insaurance_expiry_date: "",
          total_financed_amount: "",
          emi_amount: "",
          emi_start_date: "",
          emi_end_date: "",
          emi_cycle: "",
          finance_company_name: "",
          key_features: "",
          range_per_charge: "",
          charging_port_type: "",
          top_speed: "",
          tire_details: "",
          registration_certificate: "",
          insaurance_document: "",
          pollution_certificate: "",
          invoice_document: "",
          loan_lease_details: "",
        },
      ],
    },
    validationSchema: Yup.object({
      vehicles: Yup.array().of(
        Yup.object({
          vehicle_type: Yup.string()
            .required("Vehicle Type is required")
            .nullable(false),
          make: Yup.string().required("Make is required"),
          model: Yup.string().required("Model is required"),
          year_of_manufacture: Yup.string().required(
            "Year of Manufacture is required"
          ),
          registration_number: Yup.string().required(
            "Registration Number is required"
          ),
          owner_name: Yup.string().required("Owner Name is required"),
          usage_type: Yup.string()
            .required("Usage Type is required")
            .nullable(false),
        })
      ),
    }),
    onSubmit,
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
    setFieldValue,
    setFieldTouched,
  } = formik;

  useEffect(() => {
    if (vehicleData) {
      const vehicles = vehicleData.data.map((vehicle) => ({
        id: vehicle.vehicle_id,
        user_pk: vehicle.user_pk,
        companyId: vehicle.companyId,
        vehicle_type: vehicle.vehicle_type || "",
        make: vehicle.make || "",
        model: vehicle.model || "",
        variant: vehicle.variant || "",
        year_of_manufacture: vehicle.year_of_manufacture || "",
        registration_number: vehicle.registration_number || "",
        chassis_number: vehicle.chassis_number || "",
        engine_number: vehicle.engine_number || "",
        vin: vehicle.vin || "",
        license_plate_state: vehicle.license_plate_state || "",
        battery_capacity: vehicle.battery_capacity || "",
        motor_type: vehicle.motor_type || "", // Replace null with empty string
        seating_capacity: vehicle.seating_capacity || "",
        color: vehicle.color || "",
        body_type: vehicle.body_type || "", // Replace null with empty string
        last_service_date: vehicle.last_service_date || "",
        service_history: vehicle.service_history || "",
        next_service_date: vehicle.next_service_date || "",
        warranty_details: vehicle.warranty_details || "",
        roadside_assistance_provider:
          vehicle.roadside_assistance_provider || "",
        owner_name: vehicle.owner_name || "",
        owner_contact: vehicle.owner_contact || "",
        usage_type: vehicle.usage_type || "",
        odometer_reading: vehicle.odometer_reading || "",
        vehicle_insurance_provider: vehicle.vehicle_insurance_provider || "",
        insurance_policy_number: vehicle.insurance_policy_number || "",
        insurance_expiry_date: vehicle.insurance_expiry_date || "",
        key_features: vehicle.key_features || "",
        range_per_charge: vehicle.range_per_charge || "",
        charging_port_type: vehicle.charging_port_type || "", // Replace null with empty string
        top_speed: vehicle.top_speed || "",
        tire_details: vehicle.tire_details || "",
        total_financed_amount: vehicle.total_financed_amount || "",
        emi_amount: vehicle.emi_amount || "",
        emi_start_date: vehicle.emi_start_date || "",
        emi_end_date: vehicle.emi_end_date || "",
        emi_cycle: vehicle.emi_cycle || "", // Replace null with empty string
        finance_company_name: vehicle.finance_company_name || "",
        registration_certificate: vehicle.registration_certificate || "",
        insurance_document: vehicle.insurance_document || "",
        pollution_certificate: vehicle.pollution_certificate || "",
        invoice_document: vehicle.invoice_document || "",
        loan_lease_details: vehicle.loan_lease_details || "",
      }));

      formik.setValues({
        vehicles,
      });
    }
  }, [vehicleData]);

  // 🚀 Save Vehicle Data
  //   const saveVehicle = async (vehicle) => {
  //     try {
  //       const response = await mutate({
  //         url: addVehicleUrl(teamId),
  //         method: "POST",
  //         body: vehicle,
  //       });

  //       if (response.data) {
  //         await refetch();
  //         alert("Vehicle saved successfully!");
  //         navigate("/vehicles");
  //       }
  //     } catch (error) {
  //       console.error("Error saving vehicle:", error);
  //     }
  //   };

  //   const updateVehicle = async (vehicle) => {
  //     if (!vehicle.id && !vehicle.vehicleId) {
  //       return alert("Vehicle ID is required for update.");
  //     }

  //     try {
  //       const response = await mutate({
  //         url: updateVehicleUrl(teamId, vehicle.id || vehicle.vehicleId),
  //         method: "PUT",
  //         body: vehicle,
  //       });

  //       if (response.data) {
  //         await refetch();
  //         alert("Vehicle updated successfully!");
  //       }
  //     } catch (error) {
  //       console.error("Error updating vehicle:", error);
  //     }
  //   };

  //   const removeVehicle = async (vehicleId) => {
  //     if (!vehicleId) {
  //       return alert("Vehicle ID is required for removal.");
  //     }

  //     try {
  //       const response = await mutate({
  //         url: deleteVehicleUrl(id, vehicleId),
  //         method: "DELETE",
  //       });

  //       if (response.data) {
  //         await refetch();
  //         alert("Vehicle removed successfully!");
  //       }
  //     } catch (error) {
  //       console.error("Error removing vehicle:", error);
  //     }
  //   };

  if (isFetching) return <Loader />;

  return (
    <Card sx={{ p: 3 }}>
      <FormikProvider value={formik}>
        <Form onSubmit={formik.handleSubmit}>
          <FieldArray
            name="vehicles"
            render={({ push, remove }) => (
              <>
                {formik.values.vehicles.map((vehicle, index) => (
                  <Grid container spacing={3} key={index} sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                      <Typography variant="h6">Vehicle Details</Typography>
                      <Divider />
                    </Grid>

                    {/* Vehicle Fields (unchanged) */}
                    {/* Vehicle Type */}
                    <Grid item xs={4}>
                      <FormControl
                        fullWidth
                        error={Boolean(
                          touched.vehicles?.[index]?.vehicle_type &&
                            errors.vehicles?.[index]?.vehicle_type
                        )}
                      >
                        <InputLabel>Vehicle Type</InputLabel>
                        <Select
                          {...formik.getFieldProps(
                            `vehicles[${index}].vehicle_type`
                          )}
                          value={
                            formik.values.vehicles[index].vehicle_type || ""
                          }
                        >
                          <MenuItem value="" disabled>
                            Select Vehicle Type
                          </MenuItem>
                          <MenuItem value="Car">Car</MenuItem>
                          <MenuItem value="Bike">Bike</MenuItem>
                          <MenuItem value="3-Wheeler">3-Wheeler</MenuItem>
                          <MenuItem value="Truck">Truck</MenuItem>
                        </Select>
                        {touched.vehicles?.[index]?.vehicle_type &&
                          errors.vehicles?.[index]?.vehicle_type && (
                            <FormHelperText error>
                              {errors.vehicles[index].vehicle_type}
                            </FormHelperText>
                          )}
                      </FormControl>
                    </Grid>
                    {/* Make */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Make"
                        {...formik.getFieldProps(`vehicles[${index}].make`)}
                        error={Boolean(
                          touched.vehicles?.[index]?.make &&
                            errors.vehicles?.[index]?.make
                        )}
                        helperText={
                          touched.vehicles?.[index]?.make &&
                          errors.vehicles?.[index]?.make
                        }
                      />
                    </Grid>
                    {/* Model */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Model"
                        {...formik.getFieldProps(`vehicles[${index}].model`)}
                        error={Boolean(
                          touched.vehicles?.[index]?.model &&
                            errors.vehicles?.[index]?.model
                        )}
                        helperText={
                          touched.vehicles?.[index]?.model &&
                          errors.vehicles?.[index]?.model
                        }
                      />
                    </Grid>
                    {/* Varriant */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Varriant"
                        {...formik.getFieldProps(`vehicles[${index}].variant`)}
                      />
                    </Grid>
                    {/* Year of Manufacture */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Year of Manufacture"
                        {...formik.getFieldProps(
                          `vehicles[${index}].year_of_manufacture`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.year_of_manufacture &&
                            errors.vehicles?.[index]?.year_of_manufacture
                        )}
                        helperText={
                          touched.vehicles?.[index]?.year_of_manufacture &&
                          errors.vehicles?.[index]?.year_of_manufacture
                        }
                      />
                    </Grid>
                    {/* Registration Number */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Registration Number"
                        {...formik.getFieldProps(
                          `vehicles[${index}].registration_number`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.registration_number &&
                            errors.vehicles?.[index]?.registration_number
                        )}
                        helperText={
                          touched.vehicles?.[index]?.registration_number &&
                          errors.vehicles?.[index]?.registration_number
                        }
                      />
                    </Grid>
                    {/* Chassis Number */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Chassis Number"
                        {...formik.getFieldProps(
                          `vehicles[${index}].chassis_number`
                        )}
                      />
                    </Grid>
                    {/* Engine Number */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Engine Number"
                        {...formik.getFieldProps(
                          `vehicles[${index}].engine_number`
                        )}
                      />
                    </Grid>
                    {/* Vehicle Identification Number */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Vehicle Identification Number"
                        {...formik.getFieldProps(`vehicles[${index}].vin`)}
                      />
                    </Grid>
                    {/* License Plate State/Region */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="License Plate State/Region"
                        {...formik.getFieldProps(
                          `vehicles[${index}].license_plate_state`
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        Vehicle Specifications
                      </Typography>
                      <Divider />
                    </Grid>
                    {/* Battery Capacity */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Battery Capacity"
                        {...formik.getFieldProps(
                          `vehicles[${index}].battery_capacity`
                        )}
                      />
                    </Grid>
                    {/* Motor Type */}
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <InputLabel
                          variant="outlined"
                          sx={{ background: "#fff", px: 0.5 }}
                        >
                          Motor Type
                        </InputLabel>
                        <Select
                          {...formik.getFieldProps(
                            `vehicles[${index}].motor_type`
                          )}
                          value={formik.values.vehicles[index].motor_type || ""}
                        >
                          <MenuItem value="AC">AC</MenuItem>
                          <MenuItem value="DC">DC</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Seating Capacity */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Seating Capacity"
                        {...formik.getFieldProps(
                          `vehicles[${index}].seating_capacity`
                        )}
                      />
                    </Grid>
                    {/* Color */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Color"
                        {...formik.getFieldProps(`vehicles[${index}].color`)}
                      />
                    </Grid>
                    {/* Body Type */}
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <InputLabel
                          variant="outlined"
                          sx={{ background: "#fff", px: 0.5 }}
                        >
                          Body Type
                        </InputLabel>
                        <Select
                          {...formik.getFieldProps(
                            `vehicles[${index}].body_type`
                          )}
                          value={formik.values.vehicles[index].body_type || ""}
                        >
                          <MenuItem value="Sedan">Sedan</MenuItem>
                          <MenuItem value="SUV">SUV</MenuItem>
                          <MenuItem value="Hatchback">Hatchback</MenuItem>
                          <MenuItem value="Truck">Truck</MenuItem>
                          <MenuItem value="Two Wheeler">Two Wheeler</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        Owner/Usage Information
                      </Typography>
                      <Divider />
                    </Grid>
                    {/* Owner Name */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Owner Name"
                        {...formik.getFieldProps(
                          `vehicles[${index}].owner_name`
                        )}
                        error={Boolean(
                          touched.vehicles?.[index]?.owner_name &&
                            errors.vehicles?.[index]?.owner_name
                        )}
                        helperText={
                          touched.vehicles?.[index]?.owner_name &&
                          errors.vehicles?.[index]?.owner_name
                        }
                      />
                    </Grid>
                    {/* Owner Contact */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Owner Contact"
                        {...formik.getFieldProps(
                          `vehicles[${index}].owner_contact`
                        )}
                      />
                    </Grid>
                    {/* Usage Type */}
                    <Grid item xs={4}>
                      <FormControl
                        fullWidth
                        error={Boolean(
                          touched.vehicles?.[index]?.usage_type &&
                            errors.vehicles?.[index]?.usage_type
                        )}
                      >
                        <InputLabel>Usage Type</InputLabel>
                        <Select
                          {...formik.getFieldProps(
                            `vehicles[${index}].usage_type`
                          )}
                          value={formik.values.vehicles[index].usage_type || ""}
                        >
                          <MenuItem value="" disabled>
                            Select Usage Type
                          </MenuItem>
                          <MenuItem value="Private">Private</MenuItem>
                          <MenuItem value="Commercial">Commercial</MenuItem>
                        </Select>
                        {touched.vehicles?.[index]?.usage_type &&
                          errors.vehicles?.[index]?.usage_type && (
                            <FormHelperText error>
                              {errors.vehicles[index].usage_type}
                            </FormHelperText>
                          )}
                      </FormControl>
                    </Grid>
                    {/* Odometer Reading */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Odometer Reading"
                        {...formik.getFieldProps(
                          `vehicles[${index}].odometer_reading`
                        )}
                      />
                    </Grid>
                    {/* Vehicle Insurance Provider */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Vehicle Insurance Provider"
                        {...formik.getFieldProps(
                          `vehicles[${index}].vehicle_insurance_provider`
                        )}
                      />
                    </Grid>
                    {/* Insurance Policy Number */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Insurance Policy Number"
                        {...formik.getFieldProps(
                          `vehicles[${index}].insurance_policy_number`
                        )}
                      />
                    </Grid>
                    {/* Insurance Expiry Date */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Insurance Expiry Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].insurance_expiry_date`
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        Additional Features/Specifications
                      </Typography>
                      <Divider />
                    </Grid>
                    {/* Key Features */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Key Features"
                        multiline
                        rows={4}
                        {...formik.getFieldProps(
                          `vehicles[${index}].key_features`
                        )}
                      />
                    </Grid>
                    {/* Range Per Charge */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Range Per Charge"
                        {...formik.getFieldProps(
                          `vehicles[${index}].range_per_charge`
                        )}
                      />
                    </Grid>
                    {/* Charging Port Type */}
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <InputLabel
                          variant="outlined"
                          sx={{ background: "#fff", px: 0.5 }}
                        >
                          Charging Port Type
                        </InputLabel>
                        <Select
                          {...formik.getFieldProps(
                            `vehicles[${index}].charging_port_type`
                          )}
                          value={
                            formik.values.vehicles[index].charging_port_type ||
                            ""
                          }
                        >
                          <MenuItem value="CCS2">CCS2</MenuItem>
                          <MenuItem value="Type 2">Type 2</MenuItem>
                          <MenuItem value="CHAdeMO">CHAdeMO</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Top Speed */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Top Speed"
                        {...formik.getFieldProps(
                          `vehicles[${index}].top_speed`
                        )}
                      />
                    </Grid>
                    {/* Tire Details */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Tire Details"
                        {...formik.getFieldProps(
                          `vehicles[${index}].tire_details`
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        Service and Maintenance Information
                      </Typography>
                      <Divider />
                    </Grid>
                    {/* Last Service Date */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Last Service Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].last_service_date`
                        )}
                      />
                    </Grid>
                    {/* Service History */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Service History"
                        multiline
                        rows={4}
                        {...formik.getFieldProps(
                          `vehicles[${index}].service_history`
                        )}
                      />
                    </Grid>
                    {/* Next Service Date */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Next Service Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].next_service_date`
                        )}
                      />
                    </Grid>
                    {/* Warranty Details */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Warranty Details"
                        multiline
                        rows={4}
                        {...formik.getFieldProps(
                          `vehicles[${index}].warranty_details`
                        )}
                      />
                    </Grid>
                    {/* Roadside Assistance Provider */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Roadside Assistance Provider"
                        {...formik.getFieldProps(
                          `vehicles[${index}].roadside_assistance_provider`
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">Financials</Typography>
                      <Divider />
                    </Grid>
                    {/* Total Financed Amount */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Total Financed Amount"
                        {...formik.getFieldProps(
                          `vehicles[${index}].total_financed_amount`
                        )}
                      />
                    </Grid>
                    {/* EMI Amount */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="EMI Amount"
                        {...formik.getFieldProps(
                          `vehicles[${index}].emi_amount`
                        )}
                      />
                    </Grid>
                    {/* EMI Start Date */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="EMI Start Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].emi_start_date`
                        )}
                      />
                    </Grid>
                    {/* EMI End Date */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="EMI End Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        {...formik.getFieldProps(
                          `vehicles[${index}].emi_end_date`
                        )}
                      />
                    </Grid>
                    {/* EMI Cycle */}
                    <Grid item xs={4}>
                      <FormControl fullWidth>
                        <InputLabel
                          variant="outlined"
                          sx={{ background: "#fff", px: 0.5 }}
                        >
                          EMI Cycle
                        </InputLabel>
                        <Select
                          {...formik.getFieldProps(
                            `vehicles[${index}].emi_cycle`
                          )}
                          value={formik.values.vehicles[index].emi_cycle || ""}
                        >
                          <MenuItem value="Weekly">Weekly</MenuItem>
                          <MenuItem value="Bi-weekly">Bi-weekly</MenuItem>
                          <MenuItem value="Monthly">Monthly</MenuItem>
                          <MenuItem value="Quarterly">Quarterly</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Finance Company Name */}
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label="Finance Company Name"
                        {...formik.getFieldProps(
                          `vehicles[${index}].finance_company_name`
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">Documentation</Typography>
                      <Divider />
                    </Grid>
                    {/* Registration Certificate */}
                    <Grid item xs={4}>
                      <Button variant="contained" component="label">
                        Upload Registration Certificate (PDF)
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={(e) =>
                            handleFileUpload(
                              e,
                              "registration_certificate",
                              index
                            )
                          }
                        />
                      </Button>
                      {vehicle.registration_certificate && (
                        <Typography variant="body2">
                          Uploaded:{" "}
                          <a
                            href={vehicle.registration_certificate}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </Typography>
                      )}
                    </Grid>
                    {/* Insurance Document */}
                    <Grid item xs={4}>
                      <Button variant="contained" component="label">
                        Upload Insurance Document (PDF)
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={(e) =>
                            handleFileUpload(e, "insurance_document", index)
                          }
                        />
                      </Button>
                      {vehicle.insurance_document && (
                        <Typography variant="body2">
                          Uploaded:{" "}
                          <a
                            href={vehicle.insurance_document}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </Typography>
                      )}
                    </Grid>
                    {/* Pollution Certificate */}
                    <Grid item xs={4}>
                      <Button variant="contained" component="label">
                        Upload Pollution Certificate (PDF)
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={(e) =>
                            handleFileUpload(e, "pollution_certificate", index)
                          }
                        />
                      </Button>
                      {vehicle.pollution_certificate && (
                        <Typography variant="body2">
                          Uploaded:{" "}
                          <a
                            href={vehicle.pollution_certificate}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </Typography>
                      )}
                    </Grid>
                    {/* Invoice Document */}
                    <Grid item xs={4}>
                      <Button variant="contained" component="label">
                        Upload Invoice Document (PDF)
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={(e) =>
                            handleFileUpload(e, "invoice_document", index)
                          }
                        />
                      </Button>
                      {vehicle.invoice_document && (
                        <Typography variant="body2">
                          Uploaded:{" "}
                          <a
                            href={vehicle.invoice_document}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </Typography>
                      )}
                    </Grid>
                    {/* Loan/Lease Details */}
                    <Grid item xs={4}>
                      <Button variant="contained" component="label">
                        Upload Loan/Lease Details (PDF)
                        <input
                          type="file"
                          hidden
                          accept="application/pdf"
                          onChange={(e) =>
                            handleFileUpload(e, "loan_lease_details", index)
                          }
                        />
                      </Button>
                      {vehicle.loan_lease_details && (
                        <Typography variant="body2">
                          Uploaded:{" "}
                          <a
                            href={vehicle.loan_lease_details}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View
                          </a>
                        </Typography>
                      )}
                    </Grid>

                    {/* Save/Update/Remove Buttons */}
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={2}>
                        <LoadingButton
                          size="large"
                          type="submit"
                          variant="contained"
                          loading={isSubmitting}
                        >
                          Save
                        </LoadingButton>
                        <LoadingButton
                          size="large"
                          type="button"
                          variant="outlined"
                          component={RouterLink}
                          to="/vehicles"
                        >
                          Cancel
                        </LoadingButton>
                      </Stack>
                    </Grid>
                  </Grid>
                ))}
              </>
            )}
          />
        </Form>
      </FormikProvider>
    </Card>
  );
};

export default VehicleDetailsForm;
